/**
 * Persisted user preferences. Each property has a sensible auto-derived
 * default; users override via the settings drawer.
 */
export type QualityPreset = "low" | "medium" | "high";

export interface Settings {
  quality: QualityPreset;
  audioVolume: number;       // 0..1
  muted: boolean;
  reducedMotion: boolean;
  colorblindSafe: boolean;
  practice: boolean;         // Endless run, no score submission
  gyro: boolean;             // DeviceOrientation tilt-to-steer (mobile)
  daily: boolean;            // Today's deterministic seed, local-only run
  shipVariant: string;       // Cosmetic ship palette id; "default" if locked
  ghost: boolean;            // Replay last run as a translucent ghost alongside
  speedrun: boolean;         // Race-to-10k mode: end run on score >= target
  hueShift: number;          // 0-360 cosmetic hue rotation applied on top of variant
  modifiers: boolean;        // Each run picks a random gameplay modifier
  trailVariant: string;      // Streak-trail palette id; unlocks gate on mastery rank
  endless: boolean;          // Endless mode — no time burn, separate leaderboard scope
}

const KEY = "aether.settings";

export const SETTING_DEFAULTS: Settings = {
  quality: "high",
  audioVolume: 0.7,
  muted: false,
  reducedMotion: false,
  colorblindSafe: false,
  practice: false,
  gyro: false,
  daily: false,
  shipVariant: "default",
  ghost: false,
  speedrun: false,
  hueShift: 0,
  modifiers: false,
  trailVariant: "ribbon",
  endless: false,
};

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return autoDetectDefaults();
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return { ...autoDetectDefaults(), ...parsed };
  } catch {
    return autoDetectDefaults();
  }
}

export function saveSettings(s: Settings) {
  localStorage.setItem(KEY, JSON.stringify(s));
}

function autoDetectDefaults(): Settings {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return {
    ...SETTING_DEFAULTS,
    quality: detectQuality(),
    reducedMotion: reduced,
  };
}

/**
 * Best-effort GPU tier detection. Reads the WEBGL_debug_renderer_info
 * extension; falls back to devicePixelRatio heuristic if blocked.
 *
 * iOS Safari is a *special case*: it blocks WEBGL_debug_renderer_info for
 * privacy, so we get no GPU string. The DPR fallback would tag iPhones as
 * "medium" because they have DPR ≥ 2, but iOS Mobile Safari's WebGL2
 * fragment throughput + the cost of the bloom pass at DPR 3 means
 * "medium" is too aggressive in practice. Force "low" on iPhone — the
 * auto-downgrade loop will catch it eventually anyway, but starting low
 * avoids the visible jank window before the engine reacts.
 */
import { isIOSPhone } from "./ios";

export function detectQuality(): QualityPreset {
  // Hard override: iPhones start at "low" regardless of GPU detect.
  if (isIOSPhone()) return "low";

  const c = document.createElement("canvas");
  const gl = c.getContext("webgl2") || c.getContext("webgl");
  if (!gl) return "low";
  const ext = (gl as WebGLRenderingContext).getExtension("WEBGL_debug_renderer_info");
  let renderer = "";
  if (ext) {
    renderer = (gl as WebGLRenderingContext).getParameter(
      (ext as { UNMASKED_RENDERER_WEBGL: number }).UNMASKED_RENDERER_WEBGL,
    ) as string;
  }
  renderer = (renderer || "").toLowerCase();

  // Heuristic order matters — match most-specific first.
  if (/apple\s*(m\d|gpu|a1[5-9]|a2\d)|geforce|radeon\s+rx|rtx|quadro/.test(renderer)) return "high";
  if (/adreno\s+(6|7)|mali-g\d{2}|intel.*iris/.test(renderer)) return "medium";
  if (/intel.*hd|powervr|mali-(t|4|3|2)|adreno\s+(3|4|5)/.test(renderer)) return "low";
  return window.devicePixelRatio >= 2 ? "medium" : "low";
}

export interface QualityProfile {
  pixelRatioCap: number;
  bloomEnabled: boolean;
  bloomStrength: number;
  starParticleScale: number;
  maxRingDrift: number;
  /** Motion blur + FOV pulse + chromatic aberration. Off on `low` and
   *  coarse pointers (mobile) — fragment-heavy on iOS Safari. */
  motionEffects: boolean;
}

export function profileFor(q: QualityPreset): QualityProfile {
  // Mobile DPR is commonly 2.5–3×; rendering at 2× on a phone is already 9×
  // the pixel work of a 720p frame. Even "high" caps below the native DPR to
  // protect framerate. Auto-downgrade in main.ts steps down further if the
  // engine reports sustained slow frames.
  //
  // iOS Safari is uniquely slow at fragment work + the bloom pass:
  // - "low" stays at 1.0× with bloom off — the iOS-default per detectQuality()
  // - "medium" / "high" cap pixelRatio harder than other browsers
  const ios = typeof window !== "undefined" && /iPhone|iPod/.test(navigator.userAgent);
  // motionEffects: keep on for desktop med/high. Skip on iOS (afterimage pass
  // adds extra fragment work) and on low quality (auto-downgrade target).
  switch (q) {
    case "low":
      return {
        pixelRatioCap: 1.0,
        bloomEnabled: false,
        bloomStrength: 0,
        starParticleScale: 0.5,
        maxRingDrift: 1,
        motionEffects: false,
      };
    case "medium":
      return {
        pixelRatioCap: ios ? 1.0 : 1.25,
        bloomEnabled: !ios,
        bloomStrength: ios ? 0 : 0.45,
        starParticleScale: 0.8,
        maxRingDrift: 1,
        motionEffects: !ios,
      };
    case "high":
      return {
        pixelRatioCap: ios ? 1.25 : 1.75,
        bloomEnabled: true,
        bloomStrength: ios ? 0.4 : 0.7,
        starParticleScale: 1.0,
        maxRingDrift: 1,
        motionEffects: !ios,
      };
  }
}
